import { useSecurityContext } from '@/components/Navigation/SecurityContext'
import { OutageQueryStatusIndicator } from '@/components/SupplyManagement/Outages/components/Grid/components/OutageQueryStatusIndicator'
import { SpecificTimeOrNowFilter } from '@/components/SupplyManagement/components/SpecificTimeOrNowFilter'
import type { DateControlOptions, SupplyDirectoryHeaderFilters } from '@/components/SupplyManagement/types'
import { PlusCircleOutlined } from '@ant-design/icons'
import { useMetadata } from '@api/useMetadata'
import { usePrices } from '@api/usePrices'
import { ExpandCollapseButtons } from '@components/_SiteWide/ExpandCollapseButtons'
import { UploadDownloadButtons } from '@components/_SiteWide/UploadStatus/UploadDownloadButtons'
import { GraviButton, Horizontal, Texto } from '@gravitate-js/excalibrr'
import api from '@utils/api'
import type { GridApi } from 'ag-grid-community'
import { Switch } from 'antd'
import dayjs from 'dayjs'
import { ComparePricesPopover } from './ComparePricesPopover'
import type { SupplyDirectoryGridQueryFilters } from '../../types'

type PriceManagementActionButtonsProps = {
  filters: SupplyDirectoryGridQueryFilters
  headerFilters: SupplyDirectoryHeaderFilters
  setHeaderFilters: (value: SupplyDirectoryHeaderFilters) => void
  gridAPIRef: React.RefObject<GridApi>
  handleEditOutage: (outageData?: Gravitate.SupplyOutage) => void
  setExpandedGroups: React.Dispatch<React.SetStateAction<string[]>>
  isExpanded: boolean
  setIsExpanded: React.Dispatch<React.SetStateAction<boolean>>
  isRefetching?: boolean
}

export function PriceManagementActionButtons({
  filters,
  headerFilters,
  setHeaderFilters,
  gridAPIRef,
  handleEditOutage,
  setExpandedGroups,
  isExpanded,
  setIsExpanded,
  isRefetching,
}: PriceManagementActionButtonsProps) {
  const { scopes } = useSecurityContext()

  const { uploadPricesMutation } = usePrices()
  const { data: priceManagementMetadata } = useMetadata('price_management')

  function handleUpdateDate(newDateTime: string, dateControl: DateControlOptions) {
    const updatedFilter = { ...headerFilters.filter, as_of: newDateTime }
    setHeaderFilters({ ...headerFilters, filter: updatedFilter, dateControl })
  }

  const expandOverride = () => {
    setExpandedGroups(() => ['all'])
    setIsExpanded(() => true)
    gridAPIRef?.current?.expandAll()
  }
  const collapseOverride = () => {
    setExpandedGroups(() => [])
    setIsExpanded(() => false)

    gridAPIRef?.current?.collapseAll()
  }
  return (
    <>
      <OutageQueryStatusIndicator isLoading={isRefetching} />
      <UploadDownloadButtons
        fileName={`${dayjs(headerFilters.filter?.as_of).format('YYYY-MM-DD')}-SupplyPrices.xlsx`}
        excelOptions={[
          {
            title: 'Download Prices',
            downloadEP: () => {
              const header = {...headerFilters.filter, ...(headerFilters.dateControl === 'Now' ? { as_of: dayjs().toISOString() } : {}) }
              return api.downloadPriceExcel({ filter: { ...filters, ...header }, price_limit: 5 })
            },
          },
          scopes?.price_management?.edit && { title: 'Upload Prices', uploadEP: uploadPricesMutation.mutateAsync },
          {
            title: 'Legacy Price Download',
            downloadEP: () => api.downloadPriceExcelLegacy(headerFilters.dateControl === 'Now' ? dayjs().toISOString() : headerFilters.filter?.as_of),
          },
        ]}
      />
      <ExpandCollapseButtons
        gridAPIRef={gridAPIRef}
        isExpanded={isExpanded}
        collapseOverride={collapseOverride}
        expandOverride={expandOverride}
      />
      <ComparePricesPopover metadata={priceManagementMetadata} filters={headerFilters} setFilters={setHeaderFilters} />
      <Horizontal className='mx-2'>
        <Switch
          checked={headerFilters?.includePlannedVolume}
          onChange={(checked) => {
            setHeaderFilters({ ...headerFilters, includePlannedVolume: checked })
          }}
        />
        <Texto className='ml-2'>Include Planned</Texto>
      </Horizontal>

      <SpecificTimeOrNowFilter
        filters={{ as_of: headerFilters.filter?.as_of, date_control: headerFilters.dateControl }}
        onChange={handleUpdateDate}
      />
      <GraviButton buttonText='Create Outage' onClick={() => handleEditOutage(null)} icon={<PlusCircleOutlined />} />
    </>
  )
}
